import {
    alpha,
    Box,
    Container,
    styled,
    Typography,
    useMediaQuery,
    useTheme
} from '@mui/material';
import { motion, Variants } from 'framer-motion';
import React from 'react';
import { getLocalizationFromDescriptor } from '../../../../Helpers/TextDescriptorUtils';
import { getOrDefault } from '../../../../Helpers/Utils';
import { useMicrospecThemeLanguageContext } from '../../../../Themes';
import { SequentialFeature, SequentialFeaturesFlowProps } from './types';

// Styled glassmorphic feature card
const FeatureCard = styled(Box)(({ theme }) => ({
    position: 'relative',
    borderRadius: 24,
    padding: theme.spacing(4),
    background: theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.05)'
        : 'rgba(255, 255, 255, 0.8)',
    backdropFilter: 'blur(20px)',
    border: theme.palette.mode === 'dark'
        ? '1px solid rgba(255, 255, 255, 0.1)'
        : '1px solid rgba(255, 255, 255, 0.3)',
    boxShadow: theme.palette.mode === 'dark'
        ? '0 8px 32px 0 rgba(0, 0, 0, 0.5)'
        : '0 8px 32px 0 rgba(0, 0, 0, 0.1)',
    transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
    overflow: 'hidden',
    zIndex: 9,
    '&::before': {
        content: '""',
        position: 'absolute',
        top: "-4px",
        left: 0,
        right: 0,
        height: '10px',
        background: 'linear-gradient(90deg, transparent, currentColor, transparent)',
        opacity: 0,
        transition: 'opacity 0.5s ease'
    },
    '&:hover': {
        transform: 'translateY(-8px) scale(1.02)',
        boxShadow: theme.palette.mode === 'dark'
            ? '0 20px 60px 0 rgba(0, 0, 0, 0.7)'
            : '0 20px 60px 0 rgba(0, 0, 0, 0.2)',
        background: theme.palette.mode === 'dark'
            ? 'rgba(255, 255, 255, 0.08)'
            : 'rgba(255, 255, 255, 0.95)',
        '&::before': {
            opacity: 1
        }
    }
}));

// Animated step number badge
const StepBadge = styled(motion.div)(({ theme }) => ({
    position: 'absolute',
    top: -20,
    left: -20,
    width: 80,
    height: 80,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 900,
    fontSize: '2rem',
    background: 'linear-gradient(135deg, currentColor 0%, transparent 100%)',
    border: '3px solid currentColor',
    backdropFilter: 'blur(10px)',
    zIndex: 10,
    boxShadow: theme.palette.mode === 'dark'
        ? '0 8px 32px rgba(0, 0, 0, 0.6)'
        : '0 8px 32px rgba(0, 0, 0, 0.2)',
}));

const SequentialFeaturesFlow: React.FC<SequentialFeaturesFlowProps> = ({ spec, type, index }) => {
    const theme = useTheme();
    const { localizationProvider } = useMicrospecThemeLanguageContext();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // Extract values from spec
    const backgroundColor = getOrDefault(spec.backgroundColor, theme.palette.background.default);
    const accentColor = getOrDefault(spec.accentColor, theme.palette.primary.main);

    const mainTitleTextObject = getLocalizationFromDescriptor(spec.mainTitleText);
    const mainTitleText = localizationProvider.translateByType(mainTitleTextObject);
    const mainTitleColor = getOrDefault(spec.mainTitleColor, theme.palette.text.primary);

    const secondaryTitleTextObject = getLocalizationFromDescriptor(spec.secondaryTitleText);
    const secondaryTitleText = localizationProvider.translateByType(secondaryTitleTextObject);
    const secondaryTitleColor = getOrDefault(spec.secondaryTitleColor, theme.palette.text.secondary);

    const cardTextColor = getOrDefault(spec.cardTextColor, theme.palette.text.primary);
    const features = spec.features || [];
    const orientation = getOrDefault(spec.orientation, 'vertical');
    const animationStyle = getOrDefault(spec.animationStyle, 'slide');

    // Animation variants based on style
    const getItemVariants = (itemIndex: number): Variants => {
        const baseDelay = itemIndex * 0.2;

        switch (animationStyle) {
            case 'fade':
                return {
                    hidden: { opacity: 0 },
                    visible: {
                        opacity: 1,
                        transition: {
                            duration: 0.8,
                            delay: baseDelay,
                            ease: "easeOut"
                        }
                    }
                };
            case 'scale':
                return {
                    hidden: { opacity: 0, scale: 0.5 },
                    visible: {
                        opacity: 1,
                        scale: 1,
                        transition: {
                            type: "spring",
                            stiffness: 100,
                            damping: 15,
                            delay: baseDelay
                        }
                    }
                };
            case 'pulse':
                return {
                    hidden: { opacity: 0, scale: 0.8 },
                    visible: {
                        opacity: 1,
                        scale: 1,
                        transition: {
                            duration: 0.6,
                            delay: baseDelay,
                            ease: [0.43, 0.13, 0.23, 0.96]
                        }
                    }
                };
            case 'slide':
            default:
                return {
                    hidden: {
                        opacity: 0,
                        x: orientation === 'horizontal' ? -100 : 0,
                        y: orientation === 'vertical' ? 50 : 0
                    },
                    visible: {
                        opacity: 1,
                        x: 0,
                        y: 0,
                        transition: {
                            type: "spring",
                            stiffness: 80,
                            damping: 20,
                            delay: baseDelay
                        }
                    }
                };
        }
    };

    const containerVariants: Variants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.2,
                delayChildren: 0.3
            }
        }
    };

    const renderFeature = (feature: SequentialFeature, idx: number) => {
        const titleObject = getLocalizationFromDescriptor(feature.title);
        const title = localizationProvider.translateByType(titleObject);

        const descObject = getLocalizationFromDescriptor(feature.description);
        const description = localizationProvider.translateByType(descObject);

        const highlightObject = getLocalizationFromDescriptor(feature.highlight);
        const highlight = feature.highlight ? localizationProvider.translateByType(highlightObject) : null;

        const actionTextObject = getLocalizationFromDescriptor(feature.actionText);
        const actionText = feature.actionText ? localizationProvider.translateByType(actionTextObject) : null;

        const featureColor = feature.color || accentColor;

        return (
            <Box
                key={idx}
                sx={{
                    position: 'relative',
                    mb: orientation === 'vertical' ? 6 : 0,
                    mr: orientation === 'horizontal' ? 4 : 0,
                    width: orientation === 'horizontal' ? { xs: '100%', md: 'calc(33.333% - 32px)' } : '100%'
                }}
            >
                <motion.div
                    variants={getItemVariants(idx)}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-100px" }}
                >
                    <FeatureCard
                        sx={{
                            color: theme.palette.mode === "dark" ? "#f0f0f0" : featureColor,
                            position: 'relative'
                        }}
                    >
                        {/* Step Number Badge */}
                        <StepBadge
                            initial={{ scale: 0, rotate: -180 }}
                            whileInView={{ scale: 1, rotate: 0 }}
                            viewport={{ once: true }}
                            transition={{
                                type: "spring",
                                stiffness: 200,
                                damping: 20,
                                delay: idx * 0.2
                            }}
                            sx={{ color: theme.palette.mode === "dark" ? "#f0f0f0" : featureColor }}
                        >
                            {feature.stepNumber}
                        </StepBadge>

                        {/* Icon */}
                        {feature.icon && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    mb: 2,
                                    mt: 2
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 80,
                                        height: 80,
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        background: `${theme.palette.mode === "dark" ? "#f0f0f0" : featureColor}15`,
                                        border: `2px solid ${theme.palette.mode === "dark" ? "#f0f0f0" : featureColor}30`,
                                        color: theme.palette.mode === "dark" ? "#f0f0f0" : featureColor,
                                        fontSize: '2.5rem',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            transform: 'scale(1.1) rotate(5deg)',
                                            background: `${theme.palette.mode === "dark" ? "#f0f0f0" : featureColor}25`,
                                        }
                                    }}
                                >
                                    {feature.icon}
                                </Box>
                            </Box>
                        )}

                        {/* Highlight Badge */}
                        {highlight && (
                            <Box
                                sx={{
                                    display: 'inline-flex',
                                    px: 2,
                                    py: 0.5,
                                    borderRadius: 20,
                                    background: `${theme.palette.mode === "dark" ? "#f0f0f0" : featureColor}20`,
                                    border: `1px solid ${theme.palette.mode === "dark" ? "#f0f0f0" : featureColor}50`,
                                    mb: 2,
                                    fontSize: '0.875rem',
                                    fontWeight: 600,
                                    color: theme.palette.mode === "dark" ? "#f0f0f0" : featureColor
                                }}
                            >
                                {highlight}
                            </Box>
                        )}

                        {/* Title */}
                        <Typography
                            variant="h4"
                            sx={{
                                fontWeight: 700,
                                color: cardTextColor,
                                mb: 2,
                                fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' }
                            }}
                        >
                            {title}
                        </Typography>

                        {/* Description */}
                        <Typography
                            variant="body1"
                            sx={{
                                color: alpha(cardTextColor, 0.8),
                                lineHeight: 1.8,
                                fontSize: { xs: '0.95rem', sm: '1rem' },
                                mb: actionText ? 3 : 0
                            }}
                        >
                            {description}
                        </Typography>

                        {/* Action Link */}
                        {feature.actionLink && actionText && (
                            <Box sx={{ mt: 3 }}>
                                <Typography
                                    component="a"
                                    href={feature.actionLink}
                                    target={feature.actionTarget}
                                    rel="noopener noreferrer"
                                    sx={{
                                        color: theme.palette.mode === "dark" ? "#f0f0f0" : featureColor,
                                        textDecoration: 'none',
                                        fontWeight: 600,
                                        fontSize: '1rem',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            color: alpha(theme.palette.mode === "dark" ? "#f0f0f0" : featureColor, 0.8),
                                            gap: 1.5,
                                        },
                                        '&::after': {
                                            content: '"→"',
                                            fontSize: '1.2rem',
                                            transition: 'transform 0.3s ease'
                                        }
                                    }}
                                >
                                    {actionText}
                                </Typography>
                            </Box>
                        )}
                    </FeatureCard>
                </motion.div>

                {/* Connector Line - only between features, not after last one */}
                {idx < features.length - 1 && (
                    <motion.div
                        variants={getItemVariants(idx)}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        style={{
                            position: 'absolute',
                            [orientation === 'vertical' ? 'top' : 'left']: '100%',
                            [orientation === 'vertical' ? 'left' : 'top']: '50%',
                            [orientation === 'vertical' ? 'width' : 'height']: '4px',
                            [orientation === 'vertical' ? 'height' : 'width']: '56px',
                            background: `linear-gradient(${orientation === 'vertical' ? '180deg' : '90deg'}, ${theme.palette.mode === "dark" ? "#f0f0f0" : featureColor} 0%, ${alpha(theme.palette.mode === "dark" ? "#f0f0f0" : featureColor, 0.3)} 100%)`,
                            transformOrigin: 'top',
                            zIndex: 1,
                            translateY: "-10%"
                        }}
                    />
                )}
            </Box>
        );
    };

    return (
        <Box
            id={spec.id}
            sx={{
                position: 'relative',
                py: { xs: 8, md: 12 },
                background: backgroundColor,
                overflow: 'hidden'
            }}
        >
            {/* Background floating orbs for depth */}
            <Box
                sx={{
                    position: 'absolute',
                    top: '10%',
                    left: '5%',
                    width: '400px',
                    height: '400px',
                    borderRadius: '50%',
                    background: `radial-gradient(circle, ${alpha(accentColor, 0.1)} 0%, transparent 70%)`,
                    filter: 'blur(60px)',
                    pointerEvents: 'none'
                }}
            />
            <Box
                sx={{
                    position: 'absolute',
                    bottom: '10%',
                    right: '5%',
                    width: '500px',
                    height: '500px',
                    borderRadius: '50%',
                    background: `radial-gradient(circle, ${alpha(accentColor, 0.08)} 0%, transparent 70%)`,
                    filter: 'blur(80px)',
                    pointerEvents: 'none'
                }}
            />

            <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
                {/* Header Section */}
                <Box sx={{ textAlign: 'center', mb: 8 }}>
                    {mainTitleText && (
                        <motion.div
                            initial={{ opacity: 0, y: -30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, ease: "easeOut" }}
                        >
                            <Typography
                                variant="h2"
                                sx={{
                                    fontWeight: 900,
                                    color: mainTitleColor,
                                    mb: 2,
                                    fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                                    background: theme.palette.mode === "dark" ? "linear-gradient(135deg, #f0f0f0 0%, #b0b0b0 100%)" : `linear-gradient(135deg, ${mainTitleColor} 0%, ${accentColor} 100%)`,
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    backgroundClip: 'text'
                                }}
                            >
                                {mainTitleText}
                            </Typography>
                        </motion.div>
                    )}

                    {secondaryTitleText && (
                        <motion.div
                            initial={{ opacity: 0, y: -20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
                        >
                            <Typography
                                variant="h5"
                                sx={{
                                    color: secondaryTitleColor,
                                    maxWidth: '800px',
                                    mx: 'auto',
                                    lineHeight: 1.6,
                                    fontSize: { xs: '1.1rem', sm: '1.25rem' }
                                }}
                            >
                                {secondaryTitleText}
                            </Typography>
                        </motion.div>
                    )}
                </Box>

                {/* Features Flow */}
                <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, margin: "-50px" }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: orientation === 'vertical' || isMobile ? 'column' : 'row',
                            flexWrap: orientation === 'horizontal' && !isMobile ? 'wrap' : 'nowrap',
                            alignItems: orientation === 'vertical' ? 'stretch' : 'flex-start',
                            justifyContent: orientation === 'horizontal' ? 'space-between' : 'flex-start',
                            position: 'relative'
                        }}
                    >
                        {features.map((feature, idx) => renderFeature(feature, idx))}
                    </Box>
                </motion.div>
            </Container>
        </Box>
    );
};

export default SequentialFeaturesFlow;
